import { useState, useRef, useCallback } from "react";
import {
  Camera,
  X,
  Download,
  Loader2,
  Monitor,
  FileImage,
  CheckCircle2,
  AlertTriangle,
  Maximize2,
} from "lucide-react";
import { captureScreenshot, captureFullPage } from "../../utils/screenshotExport";
import { useTemplateType, useRelativePath } from "./TemplateContext";
import { navigation as brandingNav, type NavItem } from "../../siteConfig";
import { navigation as storeNav } from "../../storeSiteConfig";

/**
 * ScreenshotExportModal
 *
 * Allows users to capture a screenshot of the current page or
 * the full scrollable page, then download it as a PNG file.
 *
 * Features:
 *   - Current viewport capture
 *   - Full-page (scroll) capture
 *   - Quality/scale selector
 *   - File-name prefix from page name
 *   - Success/error feedback
 */

type CaptureMode = "viewport" | "fullpage";
type ScaleOption = { label: string; value: number };

const SCALE_OPTIONS: ScaleOption[] = [
  { label: "\u05E8\u05D2\u05D9\u05DC (1x)", value: 1 },
  { label: "\u05D2\u05D1\u05D5\u05D4 (2x)", value: 2 },
  { label: "\u05DE\u05E7\u05E1\u05D9\u05DE\u05DC\u05D9 (3x)", value: 3 },
];

function flattenNav(items: NavItem[]): { label: string; path: string }[] {
  const flat: { label: string; path: string }[] = [];
  for (const item of items) {
    flat.push({ label: item.label, path: item.path });
    if (item.children) {
      for (const child of item.children) {
        flat.push({ label: child.label, path: child.path });
      }
    }
  }
  return flat;
}

function getCurrentPageName(relativePath: string, templateType: string): string {
  const nav = templateType === "store" ? storeNav : brandingNav;
  const flat = flattenNav(nav);
  const match = flat.find((item) => item.path === relativePath);
  return match?.label || "\u05E2\u05DE\u05D5\u05D3";
}

export function ScreenshotExportModal({ onClose }: { onClose: () => void }) {
  const templateType = useTemplateType();
  const relativePath = useRelativePath();
  const [mode, setMode] = useState<CaptureMode>("viewport");
  const [scale, setScale] = useState(2);
  const [isCapturing, setIsCapturing] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const pageName = getCurrentPageName(relativePath, templateType);

  const handleCapture = useCallback(async () => {
    setIsCapturing(true);
    setStatus("idle");
    setErrorMsg("");

    try {
      // Small delay to let the modal hiding take effect visually
      // (we'll hide the modal briefly during capture)
      const modalEl = document.querySelector("[data-screenshot-modal]") as HTMLElement | null;
      if (modalEl) modalEl.style.setProperty("visibility", "hidden", "important");

      await new Promise((r) => setTimeout(r, 100));

      const safePageName = pageName.replace(/[^a-zA-Z0-9\u0590-\u05FF\u200F\u200E]/g, "-");
      const prefix = `${safePageName}`;

      if (mode === "fullpage") {
        await captureFullPage({ filenamePrefix: prefix, scale, hideEditUI: true });
      } else {
        await captureScreenshot({ filenamePrefix: prefix, scale, hideEditUI: true });
      }

      if (modalEl) modalEl.style.removeProperty("visibility");

      setStatus("success");
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setStatus("idle"), 3000);
    } catch (err) {
      const modalEl = document.querySelector("[data-screenshot-modal]") as HTMLElement | null;
      if (modalEl) modalEl.style.removeProperty("visibility");

      console.error("[ScreenshotExport] Capture failed:", err);
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05DC\u05EA\u05D9 \u05E6\u05E4\u05D5\u05D9\u05D4 \u05D1\u05E6\u05D9\u05DC\u05D5\u05DD");
    } finally {
      setIsCapturing(false);
    }
  }, [mode, scale, pageName]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-screenshot-modal
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Camera className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-[#1a1a2e] text-base leading-tight" style={{ fontWeight: 700 }}>
                {"\u05E6\u05D9\u05DC\u05D5\u05DD \u05DE\u05E1\u05DA \u05D5\u05D9\u05D9\u05E6\u05D5\u05D0"}
              </h2>
              <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                {"\u05E2\u05DE\u05D5\u05D3 \u05E0\u05D5\u05DB\u05D7\u05D9: "}{pageName}
              </p>
            </div>
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClose(); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
          >
            <X className="w-5 h-5" />
          </span>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Capture Mode */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block" style={{ fontWeight: 500 }}>
              {"\u05E1\u05D5\u05D2 \u05E6\u05D9\u05DC\u05D5\u05DD"}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <ModeCard
                active={mode === "viewport"}
                onClick={() => setMode("viewport")}
                icon={<Monitor className="w-5 h-5" />}
                label={"\u05D0\u05D6\u05D5\u05E8 \u05E0\u05E8\u05D0\u05D4"}
                description={"\u05DE\u05D4 \u05E9\u05E0\u05E8\u05D0\u05D4 \u05D1\u05DE\u05E1\u05DA"}
              />
              <ModeCard
                active={mode === "fullpage"}
                onClick={() => setMode("fullpage")}
                icon={<Maximize2 className="w-5 h-5" />}
                label={"\u05E2\u05DE\u05D5\u05D3 \u05DE\u05DC\u05D0"}
                description={"\u05DB\u05DC \u05D4\u05E2\u05DE\u05D5\u05D3 \u05DB\u05D5\u05DC\u05DC \u05D2\u05DC\u05D9\u05DC\u05D4"}
              />
            </div>
          </div>

          {/* Quality */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block" style={{ fontWeight: 500 }}>
              {"\u05D0\u05D9\u05DB\u05D5\u05EA \u05EA\u05DE\u05D5\u05E0\u05D4"}
            </label>
            <div className="flex gap-2">
              {SCALE_OPTIONS.map((opt) => (
                <span
                  key={opt.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => setScale(opt.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setScale(opt.value); }}
                  className={`flex-1 text-center py-2 px-3 rounded-xl text-xs cursor-pointer border-2 transition-all ${
                    scale === opt.value
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                  style={{ fontWeight: scale === opt.value ? 600 : 400 }}
                >
                  {opt.label}
                </span>
              ))}
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <FileImage className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {"\u05D4\u05EA\u05DE\u05D5\u05E0\u05D4 \u05EA\u05E9\u05DE\u05E8 \u05DB\u05E7\u05D5\u05D1\u05E5 PNG \u05D1\u05D0\u05D9\u05DB\u05D5\u05EA \u05D2\u05D1\u05D5\u05D4\u05D4. \u05E8\u05DB\u05D9\u05D1\u05D9 \u05E2\u05E8\u05D9\u05DB\u05D4 (\u05E1\u05E8\u05D2\u05DC, \u05D4\u05E2\u05E8\u05D5\u05EA, \u05E4\u05D9\u05E0\u05D9\u05DD) \u05D9\u05D5\u05E1\u05EA\u05E8\u05D5 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA."}
            </p>
          </div>

          {/* Status feedback */}
          {status === "success" && (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl border border-green-200 text-green-700 text-xs">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span style={{ fontWeight: 500 }}>{"\u05D4\u05EA\u05DE\u05D5\u05E0\u05D4 \u05D4\u05D5\u05E8\u05D3\u05D4 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4!"}</span>
            </div>
          )}
          {status === "error" && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-200 text-red-700 text-xs">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span style={{ fontWeight: 500 }}>{errorMsg || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E6\u05D9\u05DC\u05D5\u05DD"}</span>
            </div>
          )}

          {/* Action button */}
          <span
            role="button"
            tabIndex={0}
            onClick={isCapturing ? undefined : handleCapture}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isCapturing) handleCapture(); }}
            className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm transition-all cursor-pointer shadow-md ${
              isCapturing
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-l from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 shadow-violet-500/20"
            }`}
            style={{ fontWeight: 600 }}
          >
            {isCapturing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {"\u05DE\u05E6\u05DC\u05DD..."}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {"\u05E6\u05DC\u05DD \u05D5\u05D4\u05D5\u05E8\u05D3 \u05EA\u05DE\u05D5\u05E0\u05D4"}
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Mode selection card ── */

function ModeCard({
  active,
  onClick,
  icon,
  label,
  description,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  description: string;
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 cursor-pointer transition-all ${
        active
          ? "border-violet-500 bg-violet-50/50 shadow-sm"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <span className={active ? "text-violet-600" : "text-gray-400"}>{icon}</span>
      <span
        className={`text-sm ${active ? "text-violet-700" : "text-gray-600"}`}
        style={{ fontWeight: 600 }}
      >
        {label}
      </span>
      <span className="text-[10px] text-gray-400 text-center">{description}</span>
    </span>
  );
}

/* ── Trigger button for the AdminBar ── */

export function ScreenshotButton() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setShowModal(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowModal(true); }}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-white/10 transition-colors cursor-pointer text-blue-200/60 hover:text-white"
        title={"\u05E6\u05D9\u05DC\u05D5\u05DD \u05DE\u05E1\u05DA"}
      >
        <Camera className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-[11px]" style={{ fontWeight: 500 }}>{"\u05E6\u05D9\u05DC\u05D5\u05DD"}</span>
      </span>
      {showModal && <ScreenshotExportModal onClose={() => setShowModal(false)} />}
    </>
  );
}